import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { CONSENT_EVENT, getConsent } from "./lib/consent";

Sentry.init({
  dsn: "https://4ab76ff9ab6a24d094a307cef4d40da7@o4511614378442752.ingest.de.sentry.io/4511614394695760",
  environment: import.meta.env.MODE,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    // Session Replay is attached lazily below — bundling it here added ~250 kB
    // of JavaScript to the very first request on every device.

  ],
  // Performance
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  tracePropagationTargets: [
    /^\//,
    /^https:\/\/swiftix\.app/,
    /^https:\/\/.*\.lovable\.app/,
    /^https:\/\/noknqfmmuqptcmpqquad\.supabase\.co/,
  ],
  // Session Replay
  replaysSessionSampleRate: import.meta.env.PROD ? 0.05 : 0,
  replaysOnErrorSampleRate: 1.0,
  // Filter noise
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    "Non-Error promise rejection captured",
  ],
});

// Attach Session Replay after the app is interactive, and only on devices that
// can afford it. Replay is loaded on demand from Sentry's CDN, so it never
// competes with the app's own JavaScript for the first paint.
if (import.meta.env.PROD) {
  const attachReplay = async () => {
    try {
      const coarsePointer = window.matchMedia?.("(pointer: coarse)").matches;
      const weakCpu = (navigator.hardwareConcurrency ?? 8) <= 4;
      const savesData = (navigator as { connection?: { saveData?: boolean } }).connection?.saveData;
      if (coarsePointer || weakCpu || savesData) return;

      // Replay is optional diagnostics: only with cookie consent, always masked.
      if (getConsent() !== "all") return;
      const integration = await Sentry.lazyLoadIntegration("replayIntegration");
      Sentry.getClient()?.addIntegration(
        integration({ maskAllText: true, maskAllInputs: true, blockAllMedia: false }),
      );
    } catch {
      // Replay is optional; errors and traces still report without it.
    }
  };

  const idle = (window as unknown as {
    requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void;
  }).requestIdleCallback;
  window.addEventListener(CONSENT_EVENT, () => { void attachReplay(); }, { once: true });
  if (idle) {
    idle(attachReplay, { timeout: 6000 });
  } else {
    setTimeout(attachReplay, 4000);
  }
}
